import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  TextField,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Card,
  Button,
  Snackbar
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import RemoveIcon from '@mui/icons-material/Remove';
import HelpIcon from '@mui/icons-material/Help';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useUpdate, useNotify, useLocaleState } from 'react-admin';
// Field requirements and validation logic now centralized in NestJS validation service

import { apiFetchResponse } from '../lib/apiFetch';
import { useProofOfPaymentWarning } from './useProofOfPaymentWarning';
import DecSchemaAdjustments from './DecSchemaAdjustments';
import {
  DISSOLUTION_LOCATION_CHOICES,
  DISSOLUTION_LOCATION_PATH,
  dissolutionDecNote,
  dissolutionLocationLabel,
  legacyDissolutionHint,
} from '../clients/dissolutionLocation';
import { filenameFromContentDisposition } from '../lib/download';

type FieldStatus = 'complete' | 'missing' | 'unknown' | 'not_applicable';

/** How the modal shows a stored answer: the sentinels read as words. */
const displayValue = (result: { field?: string; value: any; isUnknown?: boolean }): string | null => {
  if (result.isUnknown) return "Family doesn't know";
  if (result.value === 'NO_CANADIAN_SIN') return 'No Canadian SIN';
  if (result.field === DISSOLUTION_LOCATION_PATH) return dissolutionLocationLabel(result.value) ?? (result.value ? String(result.value) : null);
  return result.value ? String(result.value) : null;
};

const ValueText: React.FC<{ text: string; italic: boolean }> = ({ text, italic }) => (
  <Tooltip title={text} placement="top">
    <Typography variant="body2" color="text.secondary" fontStyle={italic ? 'italic' : undefined}>
      {text.substring(0, 40)}
      {text.length > 40 ? '...' : ''}
    </Typography>
  </Tooltip>
);

/**
 * date_time_of_death with its time set to `hhmm` in Eastern time, keeping the
 * calendar date it has in Eastern time. Onboarding stores a date with no time
 * as midnight Eastern; this is how staff add the real time from the SP-3.
 */
const withEasternTime = (current: string | null | undefined, hhmm: string): string | null => {
  if (!current || !/^\d{2}:\d{2}$/.test(hhmm)) return current ?? null;
  const tz = 'America/Toronto';
  const ymd = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date(current));
  // Guess the instant as if Eastern were UTC, then correct by Eastern's offset
  // at that instant (handles daylight saving).
  const guess = new Date(`${ymd}T${hhmm}:00Z`);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  }).formatToParts(guess);
  const n = (t: string) => Number(parts.find((x) => x.type === t)?.value);
  const asEastern = Date.UTC(n('year'), n('month') - 1, n('day'), n('hour'), n('minute'));
  return new Date(guess.getTime() + (guess.getTime() - asEastern)).toISOString();
};

interface DecFormValidationProps {
  funeralId: string | number;
}

interface ValidationResult {
  field: string;
  label: string;
  value: any;
  category: string;
  decRequirement: 'required' | 'optional' | 'not_needed';
  benefitsRequirement: 'required' | 'optional' | 'not_needed';
  // 'unknown' = the family answered "I don't know" and the form can be filed
  // without it. An "I don't know" that blocks the form comes back 'missing'.
  decStatus: FieldStatus;
  benefitsStatus: FieldStatus;
  /** The family answered "I don't know" (whether or not a form allows it). */
  isUnknown?: boolean;
  priority: number; // For sorting: 1=missing required, 2=missing optional, 3=complete
  /** Why the field is asked / where it comes from. Set on some entries in
   *  field-requirements.config.ts; surfaced as a tooltip on the field name. */
  helpText?: string;
}

interface SmsMessageData {
  message: string;
  missingFieldCount: number;
  recipientName: string;
  recipientPhone?: string;
}

const DecFormValidation: React.FC<DecFormValidationProps> = ({ funeralId }) => {
  const [validationResults, setValidationResults] = useState<ValidationResult[]>([]);
  const [benefitsType, setBenefitsType] = useState<'qpp' | 'cpp'>('qpp');
  const [statementType, setStatementType] = useState<'dec' | 'form15'>('dec');
  const [survivorApplicable, setSurvivorApplicable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [originalData, setOriginalData] = useState<any>(null);
  const [recentlyUpdatedField, setRecentlyUpdatedField] = useState<string | null>(null);
  const [fieldPositions, setFieldPositions] = useState<Record<string, number>>({});

  // SMS Message state
  const [smsMessageData, setSmsMessageData] = useState<SmsMessageData | null>(null);
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsError, setSmsError] = useState<string | null>(null);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [copySnackbarMessage, setCopySnackbarMessage] = useState('');

  // XML Download state
  const [xmlDownloading, setXmlDownloading] = useState(false);

  // Table collapse state
  const [tableCollapsed, setTableCollapsed] = useState(false);

  // Show all fields state (default: show only incomplete)
  const [showAllFields, setShowAllFields] = useState(false);

  // Funeral language state
  const [funeralLanguage, setFuneralLanguage] = useState<'en' | 'fr'>('en');

  const [updateFuneral] = useUpdate();

  // QPP only: the Proof of Payment amount may include erased death-certificate
  // lines when payments exceed the contract total.
  const proofOfPaymentWarning = useProofOfPaymentWarning(funeralId, !loading && benefitsType === 'qpp');

  // Helper function for localized SMS section labels
  const getLocalizedLabels = (language: 'en' | 'fr') => {
    if (language === 'fr') {
      return {
        sectionTitle: 'Demander les informations manquantes par SMS',
        loadingMessage: 'Chargement du message...',
        recipientLabel: 'Destinataire:',
        missingFieldsLabel: 'Champs manquants:',
        copyMessageButton: 'Copier le message'
      };
    } else {
      return {
        sectionTitle: 'Request Missing Information via SMS',
        loadingMessage: 'Loading message...',
        recipientLabel: 'Recipient:',
        missingFieldsLabel: 'Missing fields:',
        copyMessageButton: 'Copy Message'
      };
    }
  };
  const [updateClient] = useUpdate();
  const [updatePartner] = useUpdate();
  const notify = useNotify();
  const [locale] = useLocaleState();

  useEffect(() => {
    if (funeralId) {
      void fetchAndValidateData();
      void fetchSmsMessage();
    }
  }, [funeralId]);

  // Re-fetch SMS message when funeral language changes
  useEffect(() => {
    if (funeralId && !loading) {
      void fetchSmsMessage();
    }
  }, [funeralLanguage]);

  const fetchAndValidateData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch validation results from centralized validation service
      const response = await apiFetchResponse(`validation/detailed/${funeralId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to validate form data');
      }

      // The new API returns validation results directly
      const validationResults = data.data;
      setValidationResults(validationResults);
      if (data.benefitsType === 'cpp' || data.benefitsType === 'qpp') {
        setBenefitsType(data.benefitsType);
      }
      if (data.statementType === 'dec' || data.statementType === 'form15') {
        setStatementType(data.statementType);
      }
      setSurvivorApplicable(data.survivorApplicable === true);


      // We still need the original funeral data for editing, so fetch it separately
      const funeralResponse = await apiFetchResponse(`browser/validate-form-data/${funeralId}`);
      const funeralData = await funeralResponse.json();
      if (funeralData.success) {
        setOriginalData(funeralData.data);
        // Extract and set the funeral language
        const language = funeralData.data?.language === 'fr' ? 'fr' : 'en';
        setFuneralLanguage(language);
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while validating form data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSmsMessage = async () => {
    try {
      setSmsLoading(true);
      setSmsError(null);

      const response = await apiFetchResponse(`validation/missing-data-message/${funeralId}?locale=${funeralLanguage}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch SMS message: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to generate SMS message');
      }

      setSmsMessageData(data.data);
    } catch (err: any) {
      setSmsError(err.message || 'An error occurred while generating SMS message');
    } finally {
      setSmsLoading(false);
    }
  };

  const handleCopyMessage = async () => {
    if (!smsMessageData?.message) return;

    try {
      await navigator.clipboard.writeText(smsMessageData.message);
      setCopySnackbarMessage('Message copied to clipboard');
      setCopySnackbarOpen(true);
    } catch (err) {
      setCopySnackbarMessage('Failed to copy message');
      setCopySnackbarOpen(true);
    }
  };

  const handleDownloadXml = async () => {
    try {
      setXmlDownloading(true);
      
      const response = await apiFetchResponse(`validation/death-declaration-xml/${funeralId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to download XML: ${response.statusText}`);
      }

      // Get filename from Content-Disposition header or use default
      const filename = filenameFromContentDisposition(response.headers.get('Content-Disposition'), 'Cleo - DEC.xml');

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setCopySnackbarMessage('XML file downloaded successfully');
      setCopySnackbarOpen(true);
    } catch (err: any) {
      console.error('Error downloading XML:', err);
      setCopySnackbarMessage('Failed to download XML file');
      setCopySnackbarOpen(true);
    } finally {
      setXmlDownloading(false);
    }
  };

  // Validation logic now handled by centralized NestJS validation service

  const getStatusIcon = (status: FieldStatus, requirement: 'required' | 'optional' | 'not_needed') => {
    if (requirement === 'not_needed' || status === 'not_applicable') {
      return <RemoveIcon sx={{ color: 'text.disabled', fontSize: 16 }} />;
    }
    
    switch (status) {
      case 'complete':
        return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16 }} />;
      case 'unknown':
        return (
          <Tooltip title="Family doesn't know. OK to file without it." arrow>
            <HelpIcon sx={{ color: 'warning.main', fontSize: 16 }} />
          </Tooltip>
        );
      case 'missing':
        return requirement === 'required' 
          ? <ErrorIcon sx={{ color: 'error.main', fontSize: 16 }} />
          : <WarningIcon sx={{ color: 'warning.main', fontSize: 16 }} />;
      default:
        return null;
    }
  };

  const getRequirementChip = (requirement: 'required' | 'optional' | 'not_needed') => {
    switch (requirement) {
      case 'required':
        return <Chip label="Required" size="small" color="error" variant="outlined" />;
      case 'optional':
        return <Chip label="Optional" size="small" color="warning" variant="outlined" />;
      case 'not_needed':
        return <Chip label="Not Needed" size="small" color="default" variant="outlined" />;
    }
  };

  const getOverallSummary = (results: ValidationResult[]) => {
    const decMissingRequired = results.filter(r => r.decRequirement === 'required' && r.decStatus === 'missing').length;
    const decMissingOptional = results.filter(r => r.decRequirement === 'optional' && r.decStatus === 'missing').length;
    const decComplete = results.filter(r => r.decRequirement !== 'not_needed' && r.decStatus === 'complete').length;
    const decUnknown = results.filter(r => r.decRequirement !== 'not_needed' && r.decStatus === 'unknown').length;
    
    const benefitsMissingRequired = results.filter(r => r.benefitsRequirement === 'required' && r.benefitsStatus === 'missing').length;
    const benefitsMissingOptional = results.filter(r => r.benefitsRequirement === 'optional' && r.benefitsStatus === 'missing').length;
    const benefitsComplete = results.filter(r => r.benefitsRequirement !== 'not_needed' && r.benefitsStatus === 'complete').length;
    const benefitsUnknown = results.filter(r => r.benefitsRequirement !== 'not_needed' && r.benefitsStatus === 'unknown').length;
    
    return {
      dec: {
        missingRequired: decMissingRequired,
        missingOptional: decMissingOptional,
        unknown: decUnknown,
        complete: decComplete,
        total: results.filter(r => r.decRequirement !== 'not_needed').length
      },
      benefits: {
        missingRequired: benefitsMissingRequired,
        missingOptional: benefitsMissingOptional,
        unknown: benefitsUnknown,
        complete: benefitsComplete,
        total: results.filter(r => r.benefitsRequirement !== 'not_needed').length
      }
    };
  };

  const overallSummary = getOverallSummary(validationResults);
  // The statement-of-death column means "the statement this case files":
  // Form 15 in Ontario, the Declaration de deces everywhere else. It used to be
  // hidden entirely for CPP cases, because the only list behind it was the
  // Quebec DEC's — Ontario now has its own, so it always shows.
  const statementLabel = statementType === 'form15' ? 'Form 15' : 'DEC';

  // The DEC requires a date of marriage. When the family doesn't know it, the
  // XML files yesterday's date (the team's practice) and staff must add a note
  // in SIED: the XML can't carry one.
  const marriageDatePlaceholder = statementType === 'dec' && validationResults.some(
    r => r.field === 'deceased_client.date_of_marriage' && r.isUnknown && r.decRequirement !== 'not_needed',
  )
    ? new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Toronto' }).format(new Date(Date.now() - 86400000))
    : null;

  // Where the marriage was dissolved: the DEC asks it, but the XML has no
  // element for it, so staff select it on the DEC web form. Spelled out next
  // to the XML download so it isn't forgotten after the import.
  const dissolutionRow = statementType === 'dec'
    ? validationResults.find(r => r.field === DISSOLUTION_LOCATION_PATH && r.decRequirement !== 'not_needed')
    : undefined;
  const dissolutionNote = dissolutionRow
    ? dissolutionDecNote(dissolutionRow.value, dissolutionRow.isUnknown)
    : null;
  const dissolutionLegacyHint = dissolutionNote?.severity === 'warning'
    ? legacyDissolutionHint(originalData?.deceased_client)
    : null;

  // CPP files two forms when a survivor claim applies: the Death Benefit
  // (ISP-1200) and the Survivor's Pension (ISP-1300).
  const benefitsLabel = benefitsType === 'cpp'
    ? (survivorApplicable ? 'CPP Death Benefit + Survivor\u2019s Pension' : 'CPP Death Benefit')
    : 'QPP Benefits';

  // Keep every field that either form needs; a field not needed by either is
  // noise (e.g. Quebec-only SP3 on an Ontario case).
  const visibleResults = validationResults.filter(
    r => r.decRequirement !== 'not_needed' || r.benefitsRequirement !== 'not_needed',
  );

  // Filter to show only incomplete fields (priority 1 and 2) by default
  const displayedResults = showAllFields ? visibleResults : visibleResults.filter(result => result.priority < 3);

  // Helper function to capture current field positions
  const captureFieldPositions = () => {
    const positions: Record<string, number> = {};
    validationResults.forEach((result, index) => {
      positions[result.field] = index;
    });
    setFieldPositions(positions);
  };

  // Editing functionality
  const handleEditStart = (field: string, currentValue: any) => {
    captureFieldPositions(); // Capture positions before editing
    setEditingField(field);
    setEditValue(currentValue ? String(currentValue) : '');
  };

  const handleEditCancel = () => {
    setEditingField(null);
    setEditValue('');
  };

  const handleEditSave = async () => {
    if (!editingField || !originalData) return;
    // Only the three DEC answers are storable (CHECK constraint).
    if (editingField === DISSOLUTION_LOCATION_PATH && !dissolutionLocationLabel(editValue)) return;

    try {
      setSaving(true);
      
      const { resource, recordId, updateData } = buildUpdatePayload(editingField, editValue);
      const updateFunction = getUpdateFunction(resource);
      
      await updateFunction(
        resource,
        {
          id: recordId,
          data: updateData,
          previousData: getRecordData(resource, recordId)
        },
        {
          onSuccess: async () => {
            // Mark field as recently updated
            setRecentlyUpdatedField(editingField);
            
            // Update the original data and re-fetch validation results
            const updatedData = updateNestedField(originalData, editingField, editValue);
            setOriginalData(updatedData);
            
            // Re-fetch validation results from the centralized service
            await fetchAndValidateData();
            
            // Clear editing state
            const updatedField = editingField;
            setEditingField(null);
            setEditValue('');
            
            notify('Field updated successfully', { type: 'success' });
            
            // Clear the recently updated flag after a delay to allow normal sorting to resume
            setTimeout(() => {
              setRecentlyUpdatedField(prev => prev === updatedField ? null : prev);
            }, 3000);
          },
          onError: (error: any) => {
            console.error('Update error:', error);
            notify('Error updating field', { type: 'error' });
          }
        }
      );
      
    } catch (err: any) {
      console.error('Save error:', err);
      notify('Error updating field', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Helper function to build update payload based on field path
  const buildUpdatePayload = (fieldPath: string, value: string) => {
    const parts = fieldPath.split('.');
    
    // Handle different entity types
    if (parts[0] === 'deceased_client') {
      const fieldName = parts.slice(1).join('.');
      return {
        resource: 'clients',
        recordId: originalData.deceased_client?.id,
        updateData: { [fieldName]: value }
      };
    } else if (parts[0] === 'authorized_client') {
      const fieldName = parts.slice(1).join('.');
      return {
        resource: 'clients',
        recordId: originalData.authorized_client?.id,
        updateData: { [fieldName]: value }
      };
    } else if (parts[0] === 'crematorium_partner') {
      const fieldName = parts.slice(1).join('.');
      return {
        resource: 'partners',
        recordId: originalData.crematorium_partner?.id,
        updateData: { [fieldName]: value }
      };
    } else if (fieldPath === 'time_of_death') {
      // Derived field: the time goes onto date_time_of_death, keeping its
      // calendar date (Eastern time).
      return {
        resource: 'funerals',
        recordId: funeralId,
        updateData: { date_time_of_death: withEasternTime(originalData.date_time_of_death, value) },
      };
    } else {
      // Direct funeral field
      return {
        resource: 'funerals',
        recordId: funeralId,
        updateData: { [fieldPath]: value }
      };
    }
  };

  // Helper function to get the appropriate update function
  const getUpdateFunction = (resource: string) => {
    switch (resource) {
      case 'clients':
        return updateClient;
      case 'partners':
        return updatePartner;
      case 'funerals':
      default:
        return updateFuneral;
    }
  };

  // Helper function to get record data for previousData
  const getRecordData = (resource: string, recordId: string | number) => {
    switch (resource) {
      case 'clients':
        // Find the client record in originalData
        if (originalData.deceased_client?.id === recordId) {
          return originalData.deceased_client;
        } else if (originalData.authorized_client?.id === recordId) {
          return originalData.authorized_client;
        }
        return {};
      case 'partners':
        return originalData.crematorium_partner || {};
      case 'funerals':
      default:
        return originalData || {};
    }
  };

  // Helper function to update nested field in data object
  const updateNestedField = (data: any, path: string, value: string) => {
    const updatedData = JSON.parse(JSON.stringify(data)); // Deep clone
    const parts = path.split('.');
    let current = updatedData;
    
    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]]) {
        current[parts[i]] = {};
      }
      current = current[parts[i]];
    }
    
    current[parts[parts.length - 1]] = value;
    return updatedData;
  };

  // Helper function to get field type for appropriate input
  const getFieldType = (fieldPath: string): 'text' | 'email' | 'tel' | 'date' | 'time' | 'select' => {
    if (fieldPath === 'time_of_death') return 'time';
    if (fieldPath.includes('email')) return 'email';
    if (fieldPath.includes('phone') || fieldPath.includes('tel')) return 'tel';
    if (fieldPath.includes('date') || fieldPath.includes('birthdate')) return 'date';
    if (fieldPath === DISSOLUTION_LOCATION_PATH) return 'select';
    if (fieldPath.includes('sex') || fieldPath.includes('marital_status')) return 'select';
    return 'text';
  };

  // Helper function to get select options
  const getSelectOptions = (fieldPath: string): Array<{ value: string; label: string }> => {
    const same = (values: string[]) => values.map((v) => ({ value: v, label: v }));
    if (fieldPath === DISSOLUTION_LOCATION_PATH) {
      return DISSOLUTION_LOCATION_CHOICES.map((c) => ({ value: c.id, label: c.name }));
    }
    if (fieldPath.includes('sex')) {
      return same(['Male', 'Female', 'Other']);
    }
    if (fieldPath.includes('marital_status')) {
      return same(['Single', 'Married', 'Divorced', 'Widowed', 'Separated', 'Common-law']);
    }
    return [];
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Validating form data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <Typography variant="h6">Validation Error</Typography>
        <Typography variant="body2">{error}</Typography>
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        {`Document Field Validation \u2014 ${statementLabel} & ${benefitsLabel}`}
      </Typography>

      {/* Overall Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                {statementLabel} Status
              </Typography>
              <Grid container spacing={1}>
                <Grid item>
                  <Chip
                    icon={<ErrorIcon />}
                    label={`${overallSummary.dec.missingRequired} Missing Required`}
                    color="error"
                    variant={overallSummary.dec.missingRequired > 0 ? "filled" : "outlined"}
                    size="small"
                  />
                </Grid>
                <Grid item>
                  <Chip
                    icon={<WarningIcon />}
                    label={`${overallSummary.dec.missingOptional} Missing Optional`}
                    color="warning"
                    variant={overallSummary.dec.missingOptional > 0 ? "filled" : "outlined"}
                    size="small"
                  />
                </Grid>
                {overallSummary.dec.unknown > 0 && (
                  <Grid item>
                    <Tooltip title="The family doesn't know these. The form can be filed without them." arrow>
                      <Chip
                        icon={<HelpIcon />}
                        label={`${overallSummary.dec.unknown} Family doesn't know`}
                        color="warning"
                        variant="outlined"
                        size="small"
                      />
                    </Tooltip>
                  </Grid>
                )}
                <Grid item>
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={`${overallSummary.dec.complete}/${overallSummary.dec.total} Complete`}
                    color="success"
                    variant="outlined"
                    size="small"
                  />
                </Grid>
              </Grid>
              {overallSummary.dec.missingRequired === 0 && (
                <Alert severity="success" sx={{ mt: 1 }}>
                  <Typography variant="body2">{`Ready for ${statementLabel} automation!`}</Typography>
                </Alert>
              )}
              {marriageDatePlaceholder && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    {`Date of marriage unknown. The DEC XML files yesterday's date (${marriageDatePlaceholder}) because the form requires one. When you submit in SIED, add a note that the date of marriage is unknown.`}
                  </Typography>
                </Alert>
              )}
              {statementType === 'dec' && (
                <DecSchemaAdjustments funeralId={funeralId} refreshKey={validationResults} />
              )}
              {dissolutionNote && (
                <Alert severity={dissolutionNote.severity} sx={{ mt: 1 }} data-testid="dec-dissolution-note">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{dissolutionNote.text}</Typography>
                  {dissolutionLegacyHint && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>{dissolutionLegacyHint}</Typography>
                  )}
                </Alert>
              )}
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={xmlDownloading ? <CircularProgress size={16} /> : <DownloadIcon />}
                  onClick={handleDownloadXml}
                  disabled={xmlDownloading}
                  size="small"
                >
                  {xmlDownloading ? 'Downloading...' : 'Download DEC XML'}
                </Button>
              </Box>
            </Paper>
          </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {benefitsLabel} Application Status
            </Typography>
            <Grid container spacing={1}>
              <Grid item>
                    <Chip
                  icon={<ErrorIcon />}
                  label={`${overallSummary.benefits.missingRequired} Missing Required`}
                  color="error"
                  variant={overallSummary.benefits.missingRequired > 0 ? "filled" : "outlined"}
                      size="small"
                    />
              </Grid>
              <Grid item>
                    <Chip
                  icon={<WarningIcon />}
                  label={`${overallSummary.benefits.missingOptional} Missing Optional`}
                  color="warning"
                  variant={overallSummary.benefits.missingOptional > 0 ? "filled" : "outlined"}
                      size="small"
                    />
              </Grid>
              {overallSummary.benefits.unknown > 0 && (
                <Grid item>
                  <Tooltip title="The family doesn't know these. The form can be filed without them." arrow>
                    <Chip
                      icon={<HelpIcon />}
                      label={`${overallSummary.benefits.unknown} Family doesn't know`}
                      color="warning"
                      variant="outlined"
                      size="small"
                    />
                  </Tooltip>
                </Grid>
              )}
              <Grid item>
                  <Chip
                  icon={<CheckCircleIcon />}
                  label={`${overallSummary.benefits.complete}/${overallSummary.benefits.total} Complete`}
                  color="success"
                  variant="outlined"
                    size="small"
                />
              </Grid>
            </Grid>
            {overallSummary.benefits.missingRequired === 0 && (
              <Alert severity="success" sx={{ mt: 1 }}>
                <Typography variant="body2">{`Ready for ${benefitsLabel} automation!`}</Typography>
              </Alert>
            )}
            {benefitsType === 'qpp' && proofOfPaymentWarning && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                <Typography variant="body2">{proofOfPaymentWarning}</Typography>
              </Alert>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Field Details Table */}
      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Field</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>{statementLabel} Status</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>{benefitsLabel} Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  Value
                  <IconButton
                    size="small"
                    onClick={() => setTableCollapsed(!tableCollapsed)}
                    sx={{ ml: 1 }}
                  >
                    {tableCollapsed ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          {!tableCollapsed && (
          <TableBody>
            {displayedResults.map((result) => (
              <TableRow
                key={result.field}
                sx={{
                  backgroundColor: result.field === recentlyUpdatedField ? 'success.light' :
                                  result.priority === 1 ? 'error.light' :
                                  result.priority === 2 ? 'warning.light' :
                                  'inherit',
                  opacity: result.priority === 3 && result.field !== recentlyUpdatedField ? 0.8 : 1,
                  transition: 'background-color 0.3s ease',
                  border: result.field === recentlyUpdatedField ? '2px solid' : 'none',
                  borderColor: result.field === recentlyUpdatedField ? 'success.main' : 'transparent'
                }}
              >
                                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    {result.helpText ? (
                      <Tooltip title={result.helpText} arrow placement="top-start">
                        <Typography
                          variant="body2"
                          fontWeight={result.priority === 1 ? 'bold' : 'normal'}
                          sx={{ borderBottom: '1px dotted', borderColor: 'text.disabled', cursor: 'help' }}
                        >
                          {result.label}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography
                        variant="body2"
                        fontWeight={result.priority === 1 ? 'bold' : 'normal'}
                      >
                        {result.label}
                      </Typography>
                    )}
                    {result.field === recentlyUpdatedField && (
                      <Chip
                        label="Updated"
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ fontSize: '0.6rem', height: '16px' }}
                      />
                    )}
                  </Box>
                </TableCell>

                <TableCell>
                          <Typography variant="caption" color="text.secondary">
                    {result.category}
                          </Typography>
                </TableCell>

                  <TableCell align="center">
                    <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                      {getStatusIcon(result.decStatus, result.decRequirement)}
                      {getRequirementChip(result.decRequirement)}
                    </Box>
                  </TableCell>

                <TableCell align="center">
                  <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                    {getStatusIcon(result.benefitsStatus, result.benefitsRequirement)}
                    {getRequirementChip(result.benefitsRequirement)}
                  </Box>
                </TableCell>

                                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    {editingField === result.field ? (
                      // Editing mode
                      <Box display="flex" alignItems="center" gap={1} width="100%">
                        {getFieldType(result.field) === 'select' ? (
                          <FormControl size="small" fullWidth>
                            <Select
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              disabled={saving}
                            >
                              {getSelectOptions(result.field).map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <TextField
                            size="small"
                            fullWidth
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            type={getFieldType(result.field)}
                            disabled={saving}
                            InputLabelProps={
                              getFieldType(result.field) === 'date' || getFieldType(result.field) === 'time'
                                ? { shrink: true }
                                : undefined
                            }
                          />
                        )}
                        <IconButton
                          size="small"
                          onClick={handleEditSave}
                          disabled={saving}
                          color="primary"
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleEditCancel}
                          disabled={saving}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      // Display mode
                      <>
                        <Box flexGrow={1}>
                          {displayValue(result) ? (
                            <ValueText text={displayValue(result) || ''} italic={!!result.isUnknown} />
                          ) : (
                            <Typography variant="body2" color="text.disabled" fontStyle="italic">
                              No value
                            </Typography>
                          )}
                          {result.field === DISSOLUTION_LOCATION_PATH && !result.value && legacyDissolutionHint(originalData?.deceased_client) && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {legacyDissolutionHint(originalData?.deceased_client)}
                            </Typography>
                          )}
                        </Box>
                        {(result.decRequirement !== 'not_needed' || result.benefitsRequirement !== 'not_needed') && (
                          <IconButton
                            size="small"
                            onClick={() => handleEditStart(result.field, result.value)}
                            disabled={saving}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                      </>
                    )}
                    </Box>
                </TableCell>
              </TableRow>
            ))}

            {/* Show All Row */}
            {!showAllFields && validationResults.some(result => result.priority === 3) && (
              <TableRow
                sx={{
                  backgroundColor: 'grey.50',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'grey.100',
                  }
                }}
                onClick={() => setShowAllFields(true)}
              >
                <TableCell colSpan={5} sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Show all fields ({validationResults.filter(result => result.priority === 3).length} completed)
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          )}
        </Table>
      </TableContainer>

      {/* SMS Message Section */}
      <Card sx={{ mt: 3, p: 2, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
          {getLocalizedLabels(funeralLanguage).sectionTitle}
        </Typography>

        {smsLoading ? (
          <Box display="flex" alignItems="center" gap={1} py={2}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              {getLocalizedLabels(funeralLanguage).loadingMessage}
            </Typography>
          </Box>
        ) : smsError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {smsError}
          </Alert>
        ) : smsMessageData ? (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>{getLocalizedLabels(funeralLanguage).recipientLabel}</strong> {smsMessageData.recipientName}
                {smsMessageData.recipientPhone && ` (${smsMessageData.recipientPhone})`}
              </Typography>
              {smsMessageData.missingFieldCount > 0 && (
                <Typography variant="body2" color="text.secondary">
                  <strong>{getLocalizedLabels(funeralLanguage).missingFieldsLabel}</strong> {smsMessageData.missingFieldCount}
                </Typography>
              )}
            </Box>

            <TextField
              multiline
              fullWidth
              rows={8}
              value={smsMessageData.message}
              InputProps={{ readOnly: true }}
              sx={{
                '& .MuiInputBase-input': {
                  fontFamily: 'monospace',
                  fontSize: '0.875rem'
                }
              }}
            />

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyMessage}
                disabled={!smsMessageData.message}
              >
                {getLocalizedLabels(funeralLanguage).copyMessageButton}
              </Button>
            </Box>
          </>
        ) : null}
      </Card>

      {/* Copy Success/Error Snackbar */}
      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setCopySnackbarOpen(false)}
        message={copySnackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default DecFormValidation; 